/**
 * Import/export `.docx` controls for the document toolbar.
 *
 * Bridges the live TipTap editor to the server format endpoints via the pure
 * `tiptap-delta` conversion + the `docx-io` client:
 *  · export → `editor.getJSON()` → delta → `/api/documents/export/docx` → download;
 *  · import → pick `.docx` → `/api/documents/import/docx` → delta → `setContent`.
 *
 * Requests go same-origin (`/api/...`, Vite proxy in dev, same-site cookie in
 * prod) with credentials so the session authenticates the call.
 */
import { createSignal, Show } from "solid-js";
import { Download, FileUp, Loader2 } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import type { Editor } from "@tiptap/core";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { exportDocx, importDocx } from "../lib/docx-io";
import { officeApiFetch } from "../lib/office-io";
import { deltaToTiptap, tiptapToDelta } from "../lib/tiptap-delta";

// Shared cookie-BFF fetch: device cookie + `X-Active-Account` selector (a
// plain credentialed fetch 401s for real accounts — see office-io.ts).
const apiFetch = officeApiFetch;

interface DocumentActionsProps {
  /** The live editor; `null` until the editor mounts. */
  editor: () => Editor | null;
  /** Document title, used as the export filename. */
  title: () => string;
}

export function DocumentActions(props: DocumentActionsProps) {
  const { t } = useI18n();
  const [busy, setBusy] = createSignal<false | "import" | "export">(false);
  let fileInput: HTMLInputElement | undefined;

  const handleExport = async () => {
    const editor = props.editor();
    if (!editor || busy()) {
      return;
    }
    setBusy("export");
    try {
      const delta = tiptapToDelta(editor.getJSON());
      const blob = await exportDocx(delta, apiFetch);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const name = props.title().trim() || "document";
      a.download = `${name}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[documents] docx export failed:", err);
    } finally {
      setBusy(false);
    }
  };

  const handleImportFile = async (file: File) => {
    const editor = props.editor();
    if (!editor || busy()) {
      return;
    }
    setBusy("import");
    try {
      const delta = await importDocx(file, apiFetch);
      editor.commands.setContent(deltaToTiptap(delta));
    } catch (err) {
      console.error("[documents] docx import failed:", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={(el) => (fileInput = el)}
        type="file"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        class="hidden"
        data-testid="docx-import-input"
        onChange={(e) => {
          const file = e.currentTarget.files?.[0];
          if (file) {
            void handleImportFile(file);
          }
          e.currentTarget.value = "";
        }}
      />
      <Button
        variant="ghost"
        size="sm"
        class="shrink-0 gap-1.5"
        data-testid="docx-import"
        disabled={busy() !== false}
        onClick={() => fileInput?.click()}
        aria-label={t(msg`Import .docx`)}
        title={t(msg`Import .docx`)}
      >
        <Show when={busy() === "import"} fallback={<FileUp class="h-4 w-4" />}>
          <Loader2 class="h-4 w-4 animate-spin" />
        </Show>
        <span class="hidden sm:inline">{t(msg`Import`)}</span>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        class="shrink-0 gap-1.5"
        data-testid="docx-export"
        disabled={busy() !== false}
        onClick={() => void handleExport()}
        aria-label={t(msg`Export .docx`)}
        title={t(msg`Export .docx`)}
      >
        <Show when={busy() === "export"} fallback={<Download class="h-4 w-4" />}>
          <Loader2 class="h-4 w-4 animate-spin" />
        </Show>
        <span class="hidden sm:inline">{t(msg`Export`)}</span>
      </Button>
    </>
  );
}
